import { createSlice } from '@reduxjs/toolkit';

import { flattenCitysprintParcelServiceCards } from '../../../helpers/mapCitysprintQuoteToParcelSections';
import { findMatchingQuoteCard } from '../helpers';

const EMPTY_QUOTE_STATE = {
  requested: false,
  selection: null,
  selectedVehicleIndex: null,
  selectionSource: null,
  validityText: null,
  scheduleKey: null,
  quickQuoteAutoFetchFired: false,
  quickQuoteAutoMatchPending: false,
};

export function buildInitialQuoteState({ quickQuotePrefill }) {
  const hasPrefill = Boolean(
    quickQuotePrefill?.prefetchedVehicleKey ||
      quickQuotePrefill?.selectedNetworkCode
  );
  return {
    ...EMPTY_QUOTE_STATE,
    requested: hasPrefill,
    quickQuoteAutoMatchPending: hasPrefill,
  };
}

export function buildScheduleKey(collectionDate, timeHour, timeMinute) {
  return [collectionDate ?? '', timeHour ?? '', timeMinute ?? ''].join('|');
}

// eslint-disable-next-line complexity -- small function
export function buildSelectionFromCard(card) {
  return {
    section: card.section,
    vehicle: card.vehicle,
    prices: card.prices,
    selectionKey: card.selectionKey,
    vehicleIndex: card.vehicleIndex,
    ...(typeof card.serviceCode === 'string' && card.serviceCode.trim()
      ? { serviceCode: card.serviceCode.trim() }
      : {}),
    ...(typeof card.vehicleType === 'string' && card.vehicleType.trim()
      ? { vehicleType: card.vehicleType.trim() }
      : {}),
    ...(typeof card.vehicleDescription === 'string' &&
    card.vehicleDescription.trim()
      ? { vehicleDescription: card.vehicleDescription.trim() }
      : {}),
    ...(typeof card.latestDeliveryTime === 'string' &&
    card.latestDeliveryTime.trim()
      ? { latestDeliveryTime: card.latestDeliveryTime.trim() }
      : {}),
    ...(typeof card.serviceName === 'string' && card.serviceName.trim()
      ? { serviceName: card.serviceName.trim() }
      : {}),
  };
}

function findCardBySelectionKey(layout, selectionKey) {
  const target = String(selectionKey ?? '').trim();
  if (!target) {
    return null;
  }
  for (const card of flattenCitysprintParcelServiceCards(layout)) {
    if (String(card.selectionKey ?? '').trim() === target) {
      return card;
    }
  }
  return null;
}

export function findQuickQuoteMatch(
  layout,
  prefetchedVehicleKey,
  selectedNetworkCode
) {
  const flat = flattenCitysprintParcelServiceCards(layout);
  if (!flat.length) {
    return null;
  }

  const networkCode =
    selectedNetworkCode != null && String(selectedNetworkCode).trim()
      ? String(selectedNetworkCode).trim()
      : '';
  const vehicleKey =
    prefetchedVehicleKey != null && String(prefetchedVehicleKey).trim()
      ? String(prefetchedVehicleKey).trim()
      : '';

  if (networkCode) {
    const prefix = `${networkCode}-`;
    const byNetwork = flat.filter(card => {
      const key = String(card.selectionKey ?? '');
      const code = String(card.serviceCode ?? '').trim();
      return key.startsWith(prefix) || code === networkCode;
    });
    if (vehicleKey) {
      const scoped = byNetwork.find(card => card.vehicle?.key === vehicleKey);
      if (scoped) {
        return scoped;
      }
    }
    if (byNetwork.length) {
      return byNetwork[0];
    }
  }

  if (vehicleKey) {
    for (const card of flat) {
      if (card.vehicle?.key === vehicleKey) {
        return card;
      }
    }
  }

  return null;
}

function isInitialScheduleHydration(prevKey, nextKey) {
  if (!prevKey || prevKey === nextKey) {
    return false;
  }
  const prev = prevKey.split('|');
  const next = nextKey.split('|');
  return prev.every((p, i) => p === next[i] || (p === '' && next[i] !== ''));
}

export const quoteSelectionSlice = createSlice({
  name: 'quoteSelection',
  initialState: EMPTY_QUOTE_STATE,
  reducers: {
    userRequestedQuote(state) {
      state.requested = true;
      state.selection = null;
      state.selectedVehicleIndex = null;
      state.validityText = null;
      state.selectionSource = null;
      state.quickQuoteAutoMatchPending = false;
    },

    userPickedVehicle(state, { payload }) {
      const nextIndex =
        state.selectedVehicleIndex === payload.vehicleIndex
          ? null
          : payload.vehicleIndex;
      state.selectedVehicleIndex = nextIndex;
      if (nextIndex == null) {
        state.selection = null;
        state.selectionSource = 'user';
        return;
      }
      if (payload.cheapestServiceCard) {
        state.selection = buildSelectionFromCard(payload.cheapestServiceCard);
        state.selectionSource = 'auto-cheapest';
        return;
      }
      state.selection = null;
      state.selectionSource = 'user';
    },

    userPickedOption(state, { payload }) {
      state.selection = payload;
      state.selectedVehicleIndex =
        payload.vehicleIndex ?? state.selectedVehicleIndex;
      state.selectionSource = 'user';
    },

    scheduleKeyChanged(state, { payload }) {
      const { key } = payload;
      if (state.scheduleKey === null) {
        state.scheduleKey = key;
        return;
      }
      if (state.scheduleKey === key) {
        return;
      }
      if (isInitialScheduleHydration(state.scheduleKey, key)) {
        state.scheduleKey = key;
        return;
      }
      Object.assign(state, EMPTY_QUOTE_STATE, { scheduleKey: key });
    },

    addressReset() {
      return EMPTY_QUOTE_STATE;
    },

    quickQuoteAutoFetchFired(state) {
      if (state.quickQuoteAutoFetchFired) {
        return;
      }
      state.quickQuoteAutoFetchFired = true;
    },

    quickQuoteMatched(state, { payload }) {
      state.selection = payload;
      state.selectedVehicleIndex =
        payload.vehicleIndex ?? state.selectedVehicleIndex;
      state.selectionSource = 'quick-quote';
      state.quickQuoteAutoMatchPending = false;
    },

    cheapestAutoSelected(state, { payload }) {
      state.selection = payload;
      state.selectedVehicleIndex =
        payload.vehicleIndex ?? state.selectedVehicleIndex;
      state.selectionSource = 'auto-cheapest';
    },

    quickQuoteMatchAttempted(state) {
      state.quickQuoteAutoMatchPending = false;
    },

    // eslint-disable-next-line complexity -- small reducer
    layoutReconciled(state, { payload }) {
      const { layout } = payload;
      if (!layout?.vehicleCards?.length) {
        return;
      }

      if (state.selection?.selectionKey) {
        const sameRow = findCardBySelectionKey(
          layout,
          state.selection.selectionKey
        );
        if (sameRow) {
          const samePrices =
            sameRow.prices?.excl === state.selection.prices?.excl &&
            sameRow.prices?.incl === state.selection.prices?.incl;
          if (samePrices) {
            return;
          }
          state.selection = buildSelectionFromCard(sameRow);
          return;
        }
        if (state.selectionSource === 'user') {
          state.selection = null;
          state.selectedVehicleIndex = null;
          return;
        }
        if (state.selectionSource === 'quick-quote') {
          state.selection = null;
          state.selectedVehicleIndex = null;
          state.selectionSource = null;
          state.quickQuoteAutoMatchPending = true;
          return;
        }
      }

      if (state.selectionSource === 'user') {
        return;
      }
      if (state.selectionSource === 'quick-quote') {
        return;
      }
      const tierId = state.selection?.section?.id;
      const vKey = state.selection?.vehicle?.key;
      if (!tierId || !vKey) {
        if (state.selection?.selectionKey) {
          state.selection = null;
          state.selectedVehicleIndex = null;
        }
        return;
      }
      const match = findMatchingQuoteCard(layout, tierId, vKey);
      if (!match) {
        state.selection = null;
        state.selectedVehicleIndex = null;
        return;
      }
      state.selection = buildSelectionFromCard(match);
      state.selectedVehicleIndex =
        match.vehicleIndex ?? state.selectedVehicleIndex;
    },

    sectionHidden(state) {
      if (state.selection === null && state.selectedVehicleIndex === null) {
        return;
      }
      const wasQuickQuote = state.selectionSource === 'quick-quote';
      state.selection = null;
      state.selectedVehicleIndex = null;
      state.selectionSource = null;
      if (wasQuickQuote) {
        state.quickQuoteAutoMatchPending = true;
      }
    },

    fetchSucceeded(state, { payload }) {
      state.validityText = payload.validityText;
    },
  },
});

export const quoteSelectionActions = quoteSelectionSlice.actions;
export const quoteSelectionReducer = quoteSelectionSlice.reducer;
